import { useState, type FormEvent, type ReactNode } from 'react';

import { api, ErroApi } from '../api';
import { formaSugerida } from '../cartoes';
import { Aviso, Campo, Corte, Painel, useAcao } from '../comum';

interface Resposta {
  readonly pergunta: string;
  readonly explicacao: string;
  readonly consulta: {
    readonly descricao: string;
    readonly intencao: string;
    readonly filtros: readonly unknown[];
    readonly agruparPor: string | null;
  };
  readonly resultado: {
    readonly kind: string;
    readonly total?: number;
    readonly linhas?: readonly Record<string, unknown>[];
    readonly grupos?: readonly { readonly chave: string; readonly total: number }[];
  };
  readonly podeSalvarCartao: boolean;
  /**
   * A resposta em português, composta a partir dos números já apurados.
   *
   * 🔴 Antes, o texto principal da tela era a `explicacao` — a descrição da
   * consulta. *"Quais máquinas possuem Google Chrome?"* recebia **"Máquinas com
   * algum software cujo nome contém Google Chrome."** e um número solto embaixo.
   * Parecia um parser falando, não alguém respondendo.
   *
   * Ver `consultas/redacao.ts`: a frase é derivada do envelope, nunca gerada.
   */
  readonly resposta?: string;
  /** Campos do filtro que ninguém coletou. Explica um zero. */
  readonly semColeta?: readonly string[];
  readonly coberturaParcial?: readonly {
    readonly campo: string;
    readonly medidas: number;
    readonly semDado: number;
  }[];
  /** Condições que o compilador descartou: o número ficou mais amplo. */
  readonly aviso?: string | null;
  /** De onde veio a resposta — base de conhecimento, inventário, software. */
  readonly fontes?: readonly string[];
  readonly meta: { readonly modo: string; readonly duracaoTotalMs: number };
}

/**
 * Os nomes das colunas da tabela de grupos.
 *
 * 🔴 **Duas colunas certas com dois nomes errados continuam sendo uma tabela
 * errada.** O ranking de programas do parque é "Valor / Total"; a lista de
 * versões de um programa é "Versão / Máquinas". Os mesmos números, e a leitura
 * muda: sob o rótulo "Total", um `2` ao lado de `141.0.7390.55` parece a
 * quantidade de instalações, não de máquinas.
 *
 * Está fora do componente para poder ser conferido sem montar a tela.
 */
/**
 * Quantas linhas de resultado a tabela desenha.
 *
 * 🔴 O corte era silencioso, e aqui isso é pior que numa lista qualquer: a
 * pessoa perguntou justamente para saber QUANTAS, e a lista devolvida
 * contradizia o número sem dizer nada.
 */
const TETO_LINHAS = 100;

export function colunasDosGrupos(kind: string): { readonly chave: string; readonly total: string } {
  return kind === 'versoes'
    ? { chave: 'Versão', total: 'Máquinas' }
    : { chave: 'Valor', total: 'Total' };
}

const EXEMPLOS = [
  'quantas máquinas ainda estão no Windows 10?',
  'notebooks Dell com menos de 8 GB de memória',
  'máquinas sem inventário há mais de 30 dias',
];

/**
 * Chat sobre o inventário.
 *
 * ⚠️ O modelo **não escreve SQL**. Ele devolve uma consulta estruturada que o
 * servidor compila — o mesmo compilador que o Sentinela usa. Por isso a
 * explicação do que ele entendeu aparece junto do resultado: é ela que permite
 * ao usuário perceber que a pergunta foi entendida errado.
 */

export function Chat({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const [pergunta, setPergunta] = useState('');
  const [resposta, setResposta] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pensando, setPensando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [tituloCartao, setTituloCartao] = useState('');
  const acao = useAcao(aoPerderSessao);

  async function perguntar(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pergunta.trim() === '') return;
    setPensando(true);
    setErro(null);
    /**
     * 🔴 **A resposta anterior sai da tela junto com a pergunta anterior.**
     *
     * Encontrado na homologação: *"quais versões do Windows temos?"* estourou o
     * tempo do modelo, e a tela mostrou o erro **com a resposta da pergunta
     * anterior ainda embaixo** — texto, tabela e tudo. Nada ali dizia que
     * aqueles números eram de outra pergunta.
     *
     * O caso medido foi benigno (a sobra era uma recusa em texto). O mesmo
     * caminho, com a pergunta anterior tendo sido *"quais versões do Google
     * Chrome"*, mostraria a tabela de versões do Chrome logo abaixo de um erro
     * sobre Windows — e alguém anotaria aquele número.
     *
     * Limpar **antes** da chamada, e não no `catch`: durante os segundos de
     * espera a tela também não pode exibir o resultado de outra pergunta.
     */
    setResposta(null);
    try {
      setResposta(await api.post<Resposta>('/api/ia/perguntar', { pergunta }));
    } catch (err: unknown) {
      if (err instanceof ErroApi && err.precisaLogin) {
        aoPerderSessao();
        return;
      }
      setErro(err instanceof Error ? err.message : String(err));
    } finally {
      setPensando(false);
    }
  }

  return (
    <>
      <Painel
        titulo="Perguntar ao inventário"
        sub="Em português. A resposta vem do seu banco, não do modelo — ele só interpreta a pergunta."
      >
        <form onSubmit={(e) => void perguntar(e)}>
          <div className="linha">
            <div>
              <input
                value={pergunta}
                placeholder="quantas máquinas ainda estão no Windows 10?"
                onChange={(e) => setPergunta(e.target.value)}
                autoFocus
              />
            </div>
            <button className="primario encolhe" type="submit" disabled={pensando}>
              {pensando ? 'Pensando…' : 'Perguntar'}
            </button>
          </div>
        </form>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {EXEMPLOS.map((ex) => (
            <button key={ex} onClick={() => setPergunta(ex)} style={{ fontSize: 13 }}>
              {ex}
            </button>
          ))}
        </div>
      </Painel>

      {erro !== null && <Aviso tipo="erro">{erro}</Aviso>}

      {resposta !== null && (
        <Painel>
          {/*
            🔴 A resposta primeiro; a interpretação depois, e menor.

            A ordem era inversa: "Entendi como: <descrição da consulta>" ocupava
            o topo e o número aparecia solto embaixo, sem frase. Quem perguntava
            "quantas máquinas temos?" recebia um 25 sem contexto, com uma linha
            técnica por cima.

            ⚠️ A interpretação **não sai da tela**. É ela que permite perceber
            que a pergunta foi entendida errado — uma frase bem escrita sobre o
            recorte errado é pior que uma linha feia sobre o certo.
          */}
          {resposta.resposta !== undefined && resposta.resposta !== '' ? (
            <p className="resposta-humana">{resposta.resposta}</p>
          ) : (
            <>
              <Aviso>
                <strong>Entendi como:</strong> {resposta.explicacao}
              </Aviso>
              {resposta.resultado.total !== undefined && (
                <p style={{ fontSize: 32, fontWeight: 600, margin: '8px 0' }}>
                  {resposta.resultado.total}
                </p>
              )}
            </>
          )}

          {resposta.resposta !== undefined && resposta.resposta !== '' && (
            <details className="detalhes-consulta">
              <summary className="suave">Como interpretei sua pergunta</summary>
              <div className="suave" style={{ marginTop: 6, fontSize: 13 }}>
                {resposta.explicacao}
                <div style={{ marginTop: 4 }}>
                  {resposta.consulta.descricao} · modo {resposta.meta.modo} ·{' '}
                  {resposta.meta.duracaoTotalMs}ms
                </div>
              </div>
            </details>
          )}

          {/*
            🔴 Campo sem coleta mente em QUALQUER direção, não só no zero.

            Nasceu de um zero ("secure boot desligado" num parque onde ninguém
            mediu secure boot). Mas o pior caso medido foi o oposto: "quantas
            máquinas sem TPM 2.0" respondeu **25** — o parque inteiro — porque o
            campo é nulo nas 25. O número está certo e a frase é falsa.
          */}
          {resposta.semColeta !== undefined && resposta.semColeta.length > 0 && (
            <Aviso tipo="alerta">
              Cuidado: <strong>{resposta.semColeta.join(', ')}</strong> não está coletado em nenhuma
              máquina do parque. Este número descreve a <strong>falta do dado</strong>, não o estado
              das máquinas. Colete pelo KACE (Custom Inventory Rule) antes de concluir qualquer
              coisa a partir dele.
            </Aviso>
          )}

          {/*
            🔴 Cobertura PARCIAL é outro aviso, e manda para outro lugar.

            `semColeta` acima diz "ninguém mediu nada" → vá criar a coleta.
            Este diz "mediram parte" → a resposta vale para N de M, e as outras
            são indeterminadas, não conformes.

            Medido em 2026-09-14: "secure boot desligado" respondeu 0 sobre o
            subconjunto medido, calado sobre o resto. Zero que tranquiliza é
            mais caro que zero que assusta.
          */}
          {resposta.coberturaParcial !== undefined && resposta.coberturaParcial.length > 0 && (
            <Aviso tipo="alerta">
              Cobertura parcial:{' '}
              {resposta.coberturaParcial.map((c, i) => (
                <span key={c.campo}>
                  {i > 0 ? ' · ' : ''}
                  <strong>{c.campo}</strong> foi coletado em {c.medidas}{' '}
                  {c.medidas === 1 ? 'máquina' : 'máquinas'} e está sem dado em{' '}
                  <strong>{c.semDado}</strong>
                </span>
              ))}
              . Este número descreve apenas o que foi medido — as demais são{' '}
              <strong>indeterminadas</strong>, não &quot;em conformidade&quot;.
            </Aviso>
          )}

          {/*
            🔴 Filtro descartado deixa o número MAIS amplo que a pergunta.

            Quando o compilador não entende uma condição, ele a descarta e segue
            — resultado parcial é melhor que tela de erro. Só que o número passa a
            responder uma pergunta mais larga, e sem este aviso a diferença some.
            (Quando TODAS as condições caem, a rota nem responde: ver
            `descaracterizada.ts`.)
          */}
          {resposta.aviso !== undefined && resposta.aviso !== null && (
            <Aviso tipo="alerta">{resposta.aviso}</Aviso>
          )}

          {/* De onde veio: conferir a origem é metade da confiança. */}
          {resposta.fontes !== undefined && resposta.fontes.length > 0 && (
            <p className="suave fonte-resposta">Fonte: {resposta.fontes.join(' · ')}</p>
          )}

          {/*
            🔴 `.length > 0`, não só `!== undefined`.

            Uma resposta sem grupos — "não encontrei versões do AutoCAD" —
            desenhava a tabela mesmo assim: cabeçalho sozinho, sem uma linha
            embaixo. Tabela vazia parece falha de carregamento, e manda conferir
            de novo uma resposta que estava certa.

            ⚠️ E o cabeçalho segue o `kind`: numa lista de versões, "Valor" e
            "Total" descrevem versão e máquinas — as duas colunas certas com os
            dois nomes errados.
          */}
          {resposta.resultado.grupos !== undefined && resposta.resultado.grupos.length > 0 && (
            <div className="rolagem">
              <table>
                <thead>
                  <tr>
                    <th>{colunasDosGrupos(resposta.resultado.kind).chave}</th>
                    <th>{colunasDosGrupos(resposta.resultado.kind).total}</th>
                  </tr>
                </thead>
                <tbody>
                  {resposta.resultado.grupos.map((g) => (
                    <tr key={g.chave}>
                      <td>{g.chave}</td>
                      <td>{g.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
            Salvar como cartão fecha o ciclo do produto: perguntei em português,
            gostei da resposta, virou painel. Ninguém precisa aprender linguagem
            de relatório — a consulta que o modelo montou é a mesma que o cartão
            executa.
          */}
          {/*
            Resposta de software não vira cartão: o cartão reexecuta uma
            consulta, e o compilador não conhece software. Um cartão salvo daqui
            rodaria contra dispositivos e mostraria outro número — o painel
            passaria a mentir em silêncio.
          */}
          {!resposta.podeSalvarCartao && (
            <p className="suave" style={{ fontSize: 13 }}>
              Esta resposta vem do inventário de software e ainda não pode virar cartão do painel.
            </p>
          )}

          <div className="salvar-cartao" hidden={!resposta.podeSalvarCartao}>
            {!salvando ? (
              <button
                onClick={() => {
                  setSalvando(true);
                  setTituloCartao(resposta.pergunta.slice(0, 60));
                }}
              >
                ★ Salvar como cartão no meu painel
              </button>
            ) : (
              <>
                <Campo rotulo="Título do cartão">
                  <input
                    value={tituloCartao}
                    maxLength={60}
                    onChange={(e) => setTituloCartao(e.target.value)}
                    autoFocus
                  />
                </Campo>
                <div className="linha">
                  <button
                    className="primario encolhe"
                    disabled={acao.executando || tituloCartao.trim() === ''}
                    onClick={() =>
                      void acao.rodar(async () => {
                        /**
                         * Precisa existir um painel próprio para receber o cartão.
                         * O painel de fábrica é só leitura — copiá-lo na primeira
                         * vez é o que torna o painel realmente do usuário.
                         */
                        const inicial = await api.get<{
                          painel: { id: string | null; cartoes: readonly unknown[] };
                          deFabrica: boolean;
                        }>('/api/paineis/inicial');

                        let painelId = inicial.painel.id;
                        if (painelId === null || inicial.deFabrica) {
                          const novo = await api.post<{ painel: { id: string } }>('/api/paineis', {
                            nome: 'Meu painel',
                            descricao: 'Criado a partir de uma pergunta.',
                            cartoes: inicial.painel.cartoes,
                          });
                          painelId = novo.painel.id;
                        }

                        await api.post(`/api/paineis/${painelId}/cartoes`, {
                          titulo: tituloCartao.trim(),
                          forma: formaSugerida(resposta.consulta.intencao),
                          largura: resposta.consulta.intencao === 'contar' ? 1 : 2,
                          limiteAlerta: null,
                          nota: null,
                          consulta: {
                            intencao: resposta.consulta.intencao,
                            filtros: resposta.consulta.filtros,
                            agruparPor: resposta.consulta.agruparPor,
                            ordenarPor: null,
                            ordemDescendente: false,
                            limite: 50,
                            explicacao: resposta.explicacao,
                          },
                        });
                        setSalvando(false);
                        return 'Cartão salvo. Ele aparece na tela Início.';
                      })
                    }
                  >
                    Salvar
                  </button>
                  <button className="encolhe" onClick={() => setSalvando(false)}>
                    Cancelar
                  </button>
                  <span className="cresce" />
                </div>
              </>
            )}
            {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
            {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}
          </div>

          {resposta.resultado.linhas !== undefined && resposta.resultado.linhas.length > 0 && (
            <div className="rolagem">
              <table>
                <thead>
                  <tr>
                    {Object.keys(resposta.resultado.linhas[0] ?? {}).map((k) => (
                      <th key={k}>{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resposta.resultado.linhas.slice(0, TETO_LINHAS).map((l, i) => (
                    <tr key={i}>
                      {Object.values(l).map((v, j) => (
                        <td key={j}>{v === null ? '—' : String(v)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <Corte
                mostrando={Math.min(resposta.resultado.linhas.length, TETO_LINHAS)}
                total={resposta.resultado.total ?? resposta.resultado.linhas.length}
                unidade="linhas"
                comoVerTudo="Exporte pela aba Inventário para ver o conjunto inteiro."
              />
            </div>
          )}
        </Painel>
      )}
    </>
  );
}
